import { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorWithPopoverService, generatePopoverId, POPOVER_ID_DATA_ATTR, PopoverAttributes } from './popover.model';
import type { PopoverService } from './popover.service';

export const popoverPluginKey = new PluginKey('popover');

export function createPopoverPlugin(editor: Editor): Plugin {
  let currentPopoverElement: HTMLElement | null = null;

  const getPopoverService = (): PopoverService | undefined => {
    return (editor as EditorWithPopoverService).storage.popoverService;
  };

  const getPopoverContext = (target: HTMLElement) => {
    const popoverElement = target.closest<HTMLElement>('[data-popover]');
    const popoverService = getPopoverService();
    return { popoverElement, popoverService };
  };

  /**
   * Ensures a popover mark has an ID. If not, generates one and updates the mark.
   * Returns the attrs with a guaranteed ID.
   */
  const ensurePopoverId = (pos: number, attrs: PopoverAttributes): PopoverAttributes => {
    if (attrs.id) {
      return attrs;
    }

    const newId = generatePopoverId();
    const attrsWithId = { ...attrs, id: newId };

    // Update the mark in the editor to persist the ID
    editor.chain().setTextSelection(pos).extendMarkRange('popover').updateAttributes('popover', { id: newId }).run();

    return attrsWithId;
  };

  return new Plugin({
    key: popoverPluginKey,
    props: {
      handleClick: (_view, pos, event) => {
        const { popoverElement, popoverService } = getPopoverContext(event.target as HTMLElement);

        if (popoverElement) {
          const attrs = getPopoverAttributes(popoverElement, popoverService);
          // Ensure the popover has an ID before pinning
          const attrsWithId = ensurePopoverId(pos, attrs);
          popoverService?.pinPopover(popoverElement, attrsWithId);
          return true;
        }
        return false;
      },

      handleDOMEvents: {
        mouseover: (_view, event) => {
          const { popoverElement, popoverService } = getPopoverContext(event.target as HTMLElement);

          if (popoverElement && popoverElement !== currentPopoverElement) {
            currentPopoverElement = popoverElement;
            const attrs = getPopoverAttributes(popoverElement, popoverService);
            const jsonContent = JSON.stringify(attrs.jsonContent ?? { type: 'doc', content: [] });
            popoverService?.showPreview(popoverElement, {
              ...attrs,
              jsonContent,
            });
          } else if (!popoverElement && currentPopoverElement) {
            popoverService?.hidePreview();
            currentPopoverElement = null;
          }
          return false;
        },
        mouseout: (_view, event) => {
          const { popoverElement: leavingPopover, popoverService } = getPopoverContext(event.target as HTMLElement);
          const relatedTarget = event.relatedTarget as HTMLElement | null;
          const enteringPopover = relatedTarget?.closest<HTMLElement>('[data-popover]');

          if (leavingPopover && !enteringPopover) {
            popoverService?.hidePreview();
            currentPopoverElement = null;
          }
          return false;
        },
      },
    },
  });
}

function getPopoverAttributes(element: HTMLElement, popoverService: PopoverService | undefined): PopoverAttributes {
  const id = element.getAttribute(POPOVER_ID_DATA_ATTR) || undefined;
  const jsonContentStr = element.getAttribute('data-json-content');
  const linkUrl = element.getAttribute('data-link-url') || undefined;

  let jsonContent = null;
  if (jsonContentStr) {
    try {
      jsonContent = JSON.parse(jsonContentStr);
    } catch (err) {
      popoverService?.reportError('Failed to parse popover JSON content from attributes', {
        context: { jsonContentStr },
        err,
      });
    }
  }

  return {
    id,
    jsonContent,
    linkUrl,
  };
}
